import { zodResolver } from '@hookform/resolvers/zod';
import { type EmailConfig, Transaction, TransactionActivity } from '@kalos/kalos-rpc';
import {
  Button,
  Dialog,
  DialogCloseCustom,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  LoadingIcon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  toast,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useConfirm,
} from '@kalos/ui';
import {
  ArrowLeftIcon,
  AvatarIcon,
  CameraIcon,
  CheckCircledIcon,
  CopyIcon,
  CrossCircledIcon,
  FileIcon,
  Pencil1Icon,
  ReaderIcon,
  ResetIcon,
  TextAlignLeftIcon,
  TrashIcon,
  UploadIcon,
} from '@radix-ui/react-icons';
import { useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { type ComponentProps, useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Prompt } from '../../../../components/Prompt';
import { PERMISSION_NAME_RECEIPT_REVIEW } from '../../../../constants';
import { useAuth } from '../../../../context/AuthContext';
import { costCentersThatRequireJobIds } from '../../../../context/TransactionLIneItems';
import { queryKeys } from '../../../../hooks/react-query/constants';
import { useEmailClientSendMutation } from '../../../../hooks/react-query/useEmailClientQuery';
import { useEventQueryInline } from '../../../../hooks/react-query/useEventsQuery';
import { useTransactionActivityCreateMutation } from '../../../../hooks/react-query/useTransactionActivityQuery';
import {
  useTransactionDeleteMutation,
  useTransactionUpdateMutation,
} from '../../../../hooks/react-query/useTransactionQuery';
import { useUserQuery, useUserQueryInline } from '../../../../hooks/react-query/useUserQuery';
import { useImmediateVendorGetQuery } from '../../../../hooks/react-query/useVendorQuery';
import { useCopyToClipboard } from '../../../../hooks/useCopyToClipboard';
import { prettyMoney } from '../../../../tools/helpers';
import { type FormProps } from '../../../../tools/types';
import { TransactionFilesGallery } from '../../../AltGallery/main';
import { reasonSchema } from '../../../Transactions/components/schema';
import { TransactionLineItemsAction } from '../../../Transactions/components/TransactionRowAdminActions';
import { TxnActivityLogs } from '../../../Transactions/components/TxnLog';
import { EditTransaction } from '../../EditTransaction';
import { Modal } from '../../Modal';
import { TechniciansComboboxPicker } from '../../Pickers/newPickers/TechnicianComboboxPicker';
import { UploadPhotoToExistingTransaction } from '../../UploadPhotoToExistingTransaction';
import { createActivityReq, useMakeUpdateTransactionStatus } from './TransactionActions.hooks';

const EditTransactionAction = ({
  transaction,
  changeCreator,
  page,
}: { transaction: Transaction; page: number } & Pick<
  ComponentProps<typeof EditTransaction>,
  'changeCreator'
>) => {
  const [isEditingModalOpen, setIsEditingModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const loggedUserId = useAuth().user.id;
  const currentUserQuery = useUserQuery({
    filters: {
      id: loggedUserId,
    },
    select(data) {
      return {
        ...data,
        isAccountsPayableAdmin: !!data.permissionGroups.find(
          (p) => p.name === 'AccountsPayableAdmin',
        )?.name,
      };
    },
  });

  const closeEditingModal = useCallback(() => {
    setIsEditingModalOpen(false);
  }, []);

  const transactionActivityCreateMutation = useTransactionActivityCreateMutation();
  const transactionUpdateMutation = useTransactionUpdateMutation({ invalidateQueries: true });
  const vendorGetQuery = useImmediateVendorGetQuery();
  const eventQuery = useEventQueryInline();

  const updateTransaction = useCallback(
    async (transactionToSave: Transaction) => {
      setIsUpdating(true);
      try {
        const log = TransactionActivity.create();
        log.timestamp = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
        log.pageNumber = page;
        log.transactionId = transactionToSave.id;
        log.userId = loggedUserId!;
        log.description = `Updating transaction with id ${transactionToSave.id} (done by user #${loggedUserId})`;
        await transactionActivityCreateMutation.mutateAsync(log);
      } catch (err) {
        setIsUpdating(false);
        console.error(`An error occurred while uploading an activity log: ${err}`);
      }

      try {
        if (transactionToSave.vendorId) {
          const vendorResult = await vendorGetQuery({ id: Number(transactionToSave.vendorId) });
          transactionToSave.vendor = vendorResult?.vendorName || '';
          if (!transactionToSave.fieldMask) {
            transactionToSave.fieldMask = [];
          }
          transactionToSave.fieldMask.push('VendorId');
          transactionToSave.fieldMask.push('Vendor');
        }
        const req = Transaction.clone(transactionToSave);
        if (req.vendorId) req.vendorId = Number(req.vendorId);
        //this means we have permission to change it so we don't need to update the department to the job's department
        if (req.jobId && !transactionToSave.fieldMask?.includes('DepartmentId')) {
          const event = await eventQuery.fetchEvent({
            filter: {
              id: req.jobId,
            },
          });
          req.departmentId = event.departmentId;
        }
        await transactionUpdateMutation.mutateAsync(req);
        setIsEditingModalOpen(false);
      } catch (err) {
        try {
          const errLog = TransactionActivity.create();
          errLog.timestamp = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
          errLog.transactionId = transactionToSave.id;
          errLog.userId = loggedUserId!;
          errLog.description = `ERROR : An error occurred while updating a transaction: ${err}`;
          await transactionActivityCreateMutation.mutateAsync(errLog);
        } catch {
          console.error(`An error occurred while updating a transaction: ${err}`);
        }
        console.error('An error occurred while updating a transaction: ', err);
        setIsEditingModalOpen(false);
      }
      setIsEditingModalOpen(false);
      setIsUpdating(false);
    },
    [
      page,
      loggedUserId,
      transactionActivityCreateMutation,
      transactionUpdateMutation,
      eventQuery,
      vendorGetQuery,
    ],
  );

  return (
    <Dialog open={isEditingModalOpen} onOpenChange={setIsEditingModalOpen}>
      <Tooltip>
        <DialogTrigger asChild>
          <TooltipTrigger asChild>
            <Button variant="outline" className="px-2 py-2">
              <Pencil1Icon />
            </Button>
          </TooltipTrigger>
        </DialogTrigger>
        <TooltipContent>Edit this transaction</TooltipContent>
      </Tooltip>

      <DialogContent showClose={false} className="max-w-max p-0">
        <EditTransaction
          transactionInput={transaction}
          title="Edit Transaction"
          isLoading={isUpdating}
          disabled={isUpdating}
          onSave={(saved) => {
            saved.id = transaction.id;
            updateTransaction(saved);
          }}
          onClose={closeEditingModal}
          changeCreator={changeCreator}
          isAccountsPayableAdmin={currentUserQuery.data?.isAccountsPayableAdmin}
        />
      </DialogContent>
    </Dialog>
  );
};

const UploadPhotoForTransactionAction = ({ transaction }: { transaction: Transaction }) => {
  const loggedUserId = useAuth().user.id;

  const [isUploadingPhotoModalOpen, setIsUploadingPhotoModalOpen] = useState(false);
  const openUploadPhotoModal = useCallback(() => {
    setIsUploadingPhotoModalOpen(true);
  }, []);
  const closeUploadPhotoModal = useCallback(() => {
    setIsUploadingPhotoModalOpen(false);
  }, []);

  const queryClient = useQueryClient();
  return (
    <>
      <Tooltip>
        <TooltipContent>Upload File</TooltipContent>
        <TooltipTrigger asChild>
          <Button variant="outline" className="px-2 py-2" onClick={openUploadPhotoModal}>
            <UploadIcon />
          </Button>
        </TooltipTrigger>
      </Tooltip>
      <Modal open={isUploadingPhotoModalOpen} maxWidth={1000} onClose={closeUploadPhotoModal}>
        <UploadPhotoToExistingTransaction
          transactionPassed={transaction}
          loggedUserId={loggedUserId}
          onClose={closeUploadPhotoModal}
          onUpload={() => {
            queryClient.invalidateQueries({
              queryKey: [queryKeys.transaction.root, queryKeys.transaction.list],
            });
            queryClient.invalidateQueries({
              queryKey: [queryKeys.transactionDocument.root],
            });
          }}
        />
      </Modal>
    </>
  );
};

const RejectTransactionAction = ({ transaction }: { transaction: Transaction }) => {
  const loggedUserId = useAuth().user.id;
  const currentUserQuery = useUserQuery({
    filters: {
      id: loggedUserId,
    },
  });
  const { fetchUser } = useUserQueryInline();
  const emailClientSendMutation = useEmailClientSendMutation();

  const [isUpdating, setIsUpdating] = useState(false);
  const [isOpened, setIsOpened] = useState(false);
  const { makeUpdateStatus } = useMakeUpdateTransactionStatus({ shouldDeleteAfterUpdate: true });

  const dispute = useCallback(
    async ({ prompt }: { prompt: string }) => {
      setIsUpdating(true);
      if (!currentUserQuery.data) {
        // should not happen as button that calls this function is disabled if there is no user
        console.error('Need a user to send to for disputes, however none was gotten. Returning.');
        return;
      }

      if (transaction.ownerId && transaction.ownerId > 0) {
        const ownerUser = await fetchUser({
          filter: { id: transaction.ownerId },
        });

        const body = getRejectTxnBody(
          prompt,
          transaction.amount,
          transaction.description,
          transaction.vendor,
        );
        const email: EmailConfig = {
          type: 'receipts',
          recipient: ownerUser.email,
          subject: 'Receipts',
          from: currentUserQuery.data.email,
          body,
        };

        try {
          await emailClientSendMutation.mutateAsync(email);
        } catch {
          alert('An error occurred, user was not notified via email');
        }
      }

      try {
        await makeUpdateStatus({
          id: transaction.id,
          statusID: 4,
          description: 'rejected',
          reason: prompt,
        });
      } catch {
        alert('An error occurred while updating the transaction status');
      }
      setIsUpdating(false);
    },
    [
      currentUserQuery.data,
      emailClientSendMutation,
      fetchUser,
      makeUpdateStatus,
      transaction.amount,
      transaction.description,
      transaction.id,
      transaction.ownerId,
      transaction.vendor,
    ],
  );

  return (
    <Tooltip>
      <TooltipContent>Reject</TooltipContent>
      <Prompt
        onSubmit={dispute}
        onOpenChange={setIsOpened}
        promptSchema={reasonSchema}
        isLoading={isUpdating}
        open={isOpened}
        title="Reject Transaction"
        label="Enter reason for rejection:"
        trigger={
          <TooltipTrigger asChild>
            <Button
              disabled={transaction.statusId === 5 || !currentUserQuery.isSuccess}
              size="icon"
              variant="outline"
            >
              <CrossCircledIcon />
              <span className="sr-only">Reject</span>
            </Button>
          </TooltipTrigger>
        }
      />
    </Tooltip>
  );
};

const ApproveTransactionAction = ({
  transaction,
  shouldDeleteAfterUpdate,
}: {
  transaction: Transaction;
  shouldDeleteAfterUpdate?: boolean;
}) => {
  const { makeUpdateStatus } = useMakeUpdateTransactionStatus({ shouldDeleteAfterUpdate });
  const [isApproving, setIsApproving] = useState(false);
  const [showPurchaserForm, setShowPurchaserForm] = useState(false);
  const confirm = useConfirm();
  const transactionUpdateMutation = useTransactionUpdateMutation();

  const form = useForm<{ purchaserId: number }>({
    resolver: zodResolver(
      z.object({
        purchaserId: z.number().min(1, 'Please select a purchaser'),
      }),
    ),
  });

  const requestUpdateStatus = useCallback(async () => {
    if (!transaction.ownerId) {
      setShowPurchaserForm(true);
      return;
    }

    const ok = await confirm(`Are you sure you want to mark this transaction as accepted?`);
    if (!ok) return;

    try {
      setIsApproving(true);
      await makeUpdateStatus({ id: transaction.id, statusID: 3, description: 'approved' });
    } catch {
      toast({
        variant: 'destructive',
        title: 'An error occurred while updating the transaction status',
      });
    } finally {
      setIsApproving(false);
    }
  }, [confirm, makeUpdateStatus, transaction.id, transaction.ownerId]);

  const handlePurchaserSubmit = useCallback(
    async (data: { purchaserId: number }) => {
      try {
        setIsApproving(true);
        // First update the transaction with the new purchaser
        const req = Transaction.create({
          id: transaction.id,
          ownerId: data.purchaserId,
          fieldMask: ['OwnerId'],
        });
        await transactionUpdateMutation.mutateAsync(req);

        // Then approve the transaction
        await makeUpdateStatus({ id: transaction.id, statusID: 3, description: 'approved' });
        setShowPurchaserForm(false);
      } catch {
        alert('An error occurred while updating the transaction');
      } finally {
        setIsApproving(false);
      }
    },
    [makeUpdateStatus, transaction.id, transactionUpdateMutation],
  );

  const requireJobId = useMemo(
    () => costCentersThatRequireJobIds.includes(transaction.costCenterId),
    [transaction.costCenterId],
  );

  const { message: acceptActionMessage, disabled: markAsAcceptedDisabled } = useMemo<{
    message: string;
    disabled: boolean;
  }>(() => {
    if (transaction.statusId === 5) {
      return {
        message: 'Transaction is already processed',
        disabled: true,
      };
    }
    if (requireJobId && !transaction.jobId) {
      return {
        message: 'Transaction cost center requires a job ID. Please add a job ID before approve',
        disabled: true,
      };
    }

    return {
      message: 'Mark as accepted',
      disabled: false,
    };
  }, [transaction.jobId, transaction.statusId, requireJobId]);
  return (
    <>
      <Tooltip>
        <TooltipContent className="max-w-sm">
          <span>Mark as accepted</span>
          {markAsAcceptedDisabled && (
            <span className="text-red-500">&nbsp;({acceptActionMessage})</span>
          )}
        </TooltipContent>
        <TooltipTrigger asChild>
          <span>
            <Button
              disabled={markAsAcceptedDisabled}
              variant="outline"
              className="px-2 py-2"
              onClick={requestUpdateStatus}
            >
              {isApproving ? <LoadingIcon /> : <CheckCircledIcon />}
            </Button>
          </span>
        </TooltipTrigger>
      </Tooltip>

      <Dialog open={showPurchaserForm} onOpenChange={setShowPurchaserForm}>
        <DialogContent>
          <DialogTitle>Select Purchaser</DialogTitle>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handlePurchaserSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="purchaserId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Purchaser</FormLabel>
                    <FormMessage />
                    <TechniciansComboboxPicker
                      onSelect={(u) => field.onChange(u?.id ?? 0)}
                      selected={field.value?.toString() ?? ''}
                      disabled={isApproving}
                    />
                  </FormItem>
                )}
              />
              <Button type="submit" disabled={isApproving} isLoading={isApproving}>
                Approve Transaction
              </Button>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
};

const CopyTransactionInfoAction = ({ transaction }: { transaction: Transaction }) => {
  const [, copyToClipboard] = useCopyToClipboard();
  const [isSuccess, setIsSuccess] = useState(false);

  const copyTransaction = useCallback(() => {
    copyToClipboard(
      `${parseISO(transaction.timestamp.split(' ').join('T')).toLocaleDateString()},${
        transaction.description
      },${transaction.orderNumber},${transaction.invoiceNumber},${transaction.ownerName},${
        transaction.assignedEmployeeName
      },${transaction.department?.description},${transaction.jobId},${
        transaction.costCenter?.description
      },${transaction.amount},${transaction.vendor},${transaction.notes}`,
    ).then((res) => {
      setIsSuccess(res);
      if (res) {
        setTimeout(() => {
          setIsSuccess(false);
        }, 2000);
      }
    });
  }, [
    copyToClipboard,
    transaction.amount,
    transaction.assignedEmployeeName,
    transaction.costCenter?.description,
    transaction.department?.description,
    transaction.description,
    transaction.invoiceNumber,
    transaction.jobId,
    transaction.notes,
    transaction.orderNumber,
    transaction.ownerName,
    transaction.timestamp,
    transaction.vendor,
  ]);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="outline" className="px-2 py-2" onClick={copyTransaction}>
          {isSuccess ? <CheckCircledIcon /> : <CopyIcon />}
        </Button>
      </TooltipTrigger>
      <TooltipContent>Copy transaction information</TooltipContent>
    </Tooltip>
  );
};

const ProcessTransactionAction = ({
  transaction,
  shouldDeleteAfterUpdate,
}: {
  transaction: Transaction;
  shouldDeleteAfterUpdate?: boolean;
}) => {
  const { makeUpdateStatus } = useMakeUpdateTransactionStatus({ shouldDeleteAfterUpdate });
  const confirm = useConfirm();
  const [isLoading, setIsLoading] = useState(false);

  const requestUpdateStatus = useCallback(async () => {
    const ok = await confirm({
      title: `Are you sure you want to mark this transaction as Processed?`,
    });
    if (!ok) return;

    try {
      setIsLoading(true);
      await makeUpdateStatus({
        id: transaction.id,
        statusID: 5,
        description: 'Recorded and Processed',
      });
      toast({
        title: 'successfully updated transaction status to Processed!',
        variant: 'success',
      });
    } catch {
      toast({
        title: 'An error occurred while updating the transaction status',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [confirm, makeUpdateStatus, setIsLoading, transaction.id]);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          disabled={isLoading}
          isLoading={isLoading}
          variant="outline"
          className="px-2 py-2"
          onClick={requestUpdateStatus}
        >
          <FileIcon />
        </Button>
      </TooltipTrigger>
      <TooltipContent>Mark as processed</TooltipContent>
    </Tooltip>
  );
};

const DeleteTransactionAction = ({ transaction }: { transaction: Transaction }) => {
  const transactionDeleteMutation = useTransactionDeleteMutation();
  const loggedUserId = useAuth().user.id;
  const transactionActivityCreate = useTransactionActivityCreateMutation();
  const confirm = useConfirm();

  const deleteTransaction = useCallback(async () => {
    const ok = await confirm(`Are you sure you want to delete this transaction?`);
    if (!ok) return;

    try {
      await transactionDeleteMutation.mutateAsync(transaction);
      const activity = createActivityReq(
        'Accounts Payable Record Deleted',
        transaction.id,
        loggedUserId,
      );
      await transactionActivityCreate.mutateAsync(activity);
    } catch (err) {
      console.error(`An error occurred while deleting a transaction: ${err}`);
    }
  }, [confirm, transactionDeleteMutation, transaction, loggedUserId, transactionActivityCreate]);

  return (
    <Tooltip>
      <TooltipContent>Delete</TooltipContent>
      <TooltipTrigger asChild>
        <Button variant="outline" className="px-2 py-2" onClick={deleteTransaction}>
          <TrashIcon />
        </Button>
      </TooltipTrigger>
    </Tooltip>
  );
};

const assignEmployeeFormSchema = z.object({
  assignedEmployeeId: z.number().min(1, { message: 'Please select an employee to assign.' }),
});

const ReassignForm = ({
  defaultValues,
  onSave,
  disabled,
  isLoading,
}: FormProps<z.infer<typeof assignEmployeeFormSchema>>) => {
  const form = useForm<z.infer<typeof assignEmployeeFormSchema>>({
    resolver: zodResolver(assignEmployeeFormSchema),
    defaultValues,
    disabled,
  });

  const onSubmit = form.handleSubmit((data) => {
    onSave({ data, dirtyFields: form.formState.dirtyFields });
  });

  return (
    <Form {...form}>
      <form className="flex flex-col gap-2" onSubmit={onSubmit}>
        <FormField
          control={form.control}
          name="assignedEmployeeId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Employee to assign</FormLabel>
              <TechniciansComboboxPicker
                onSelect={(u) => field.onChange(u?.id ?? 0)}
                selected={field.value.toString()}
                disabled={field.disabled}
              />
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" disabled={disabled || !form.formState.isDirty} isLoading={isLoading}>
          Reassign
        </Button>
      </form>
    </Form>
  );
};

const AssignTransactionAction = ({ transaction }: { transaction: Transaction }) => {
  const [isReassignModalOpen, setIsReassignModalOpen] = useState(false);
  const transactionUpdateMutation = useTransactionUpdateMutation();

  const handleAssignEmployee = useCallback<ComponentProps<typeof ReassignForm>['onSave']>(
    async (data) => {
      try {
        const req = Transaction.create();
        req.id = transaction.id;
        req.assignedEmployeeId = data.data.assignedEmployeeId;
        req.fieldMask = ['AssignedEmployeeId'];
        await transactionUpdateMutation.mutateAsync(req);
        toast({
          title: 'Successfully assigned employee',
          variant: 'success',
        });
        setIsReassignModalOpen(false);
      } catch {
        toast({
          title: 'An error occurred while assigning an employee',
          variant: 'destructive',
        });
      }
    },
    [transaction.id, transactionUpdateMutation],
  );

  const defaultValues = useMemo(
    () => ({
      assignedEmployeeId: transaction.assignedEmployeeId,
    }),
    [transaction.assignedEmployeeId],
  );

  return (
    <Tooltip>
      <TooltipContent>Assign an employee to this task</TooltipContent>
      <Dialog open={isReassignModalOpen} onOpenChange={setIsReassignModalOpen}>
        <DialogTrigger asChild>
          <TooltipTrigger asChild>
            <Button variant="outline" className="px-2 py-2">
              <AvatarIcon />
            </Button>
          </TooltipTrigger>
        </DialogTrigger>

        <DialogContent>
          <DialogTitle>Select an employee to assign to this task</DialogTitle>
          <ReassignForm
            defaultValues={defaultValues}
            disabled={transactionUpdateMutation.isPending}
            isLoading={transactionUpdateMutation.isPending}
            onSave={handleAssignEmployee}
          />
        </DialogContent>
      </Dialog>
    </Tooltip>
  );
};

export const TransactionGalleryAction = (
  galleryProps: ComponentProps<typeof TransactionFilesGallery>,
) => (
  <Dialog>
    <Tooltip>
      <DialogTrigger asChild>
        <TooltipTrigger asChild>
          <Button className="px-2 py-2" variant="outline">
            <CameraIcon />
          </Button>
        </TooltipTrigger>
      </DialogTrigger>
      <TooltipContent>View Photos and Documents</TooltipContent>
    </Tooltip>
    <DialogContent
      showClose={false}
      className="h-[80vh] w-full max-w-none overflow-y-auto bg-none p-0 sm:w-[60vw]"
    >
      <div className="relative">
        <DialogCloseCustom className="absolute right-8 top-4  z-20" />
        <TransactionFilesGallery {...galleryProps} />
      </div>
    </DialogContent>
  </Dialog>
);

export const ViewTransactionLogsActions = ({ transactionId }: { transactionId: number }) => (
  <Dialog>
    <Tooltip>
      <DialogTrigger asChild>
        <TooltipTrigger asChild>
          <Button className="px-2 py-2" variant="outline">
            <ReaderIcon />
          </Button>
        </TooltipTrigger>
      </DialogTrigger>
      <TooltipContent>View Activity logs</TooltipContent>
    </Tooltip>
    <DialogContent
      showClose={false}
      className="flex max-h-[80vh] max-w-max overflow-y-auto border-none p-0 pt-2"
    >
      <div className="relative flex flex-1">
        <DialogCloseCustom className="absolute right-8 top-4  z-20" />
        <TxnActivityLogs transactionId={transactionId} />
      </div>
    </DialogContent>
  </Dialog>
);

const SubmitTransactionAction = ({
  transaction,
  shouldDeleteAfterUpdate,
}: {
  transaction: Transaction;
  shouldDeleteAfterUpdate?: boolean;
}) => {
  const { makeUpdateStatus } = useMakeUpdateTransactionStatus({ shouldDeleteAfterUpdate });
  const confirm = useConfirm();
  const [isLoading, setIsLoading] = useState(false);

  const updateStatusSubmit = useCallback(async () => {
    const ok = await confirm({
      title: `Are you sure you want to resubmit this transaction?`,
    });
    if (!ok) return;

    try {
      setIsLoading(true);
      await makeUpdateStatus({
        id: transaction.id,
        statusID: 2,
        description: 'Re-submitted Transaction',
      });
      toast({
        title: 'Successfully resubmitted transaction!',
        variant: 'success',
      });
    } catch {
      toast({
        title: 'An error occurred while updating the transaction status',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [confirm, makeUpdateStatus, transaction.id]);

  return (
    <Tooltip>
      <TooltipContent>Submit</TooltipContent>
      <TooltipTrigger asChild>
        <Button
          disabled={isLoading}
          isLoading={isLoading}
          variant="outline"
          className="px-2 py-2"
          onClick={updateStatusSubmit}
        >
          <ResetIcon />
        </Button>
      </TooltipTrigger>
    </Tooltip>
  );
};

const SetTransactionToPendingAction = ({
  transaction,
  shouldDeleteAfterUpdate,
}: {
  transaction: Transaction;
  shouldDeleteAfterUpdate?: boolean;
}) => {
  const { makeUpdateStatus } = useMakeUpdateTransactionStatus({ shouldDeleteAfterUpdate });
  const confirm = useConfirm();
  const [isLoading, setIsLoading] = useState(false);

  const requestUpdateStatus = useCallback(async () => {
    const ok = await confirm({
      title: `Are you sure you want to set this transaction back to Pending?`,
    });
    if (!ok) return;

    try {
      setIsLoading(true);
      await makeUpdateStatus({
        id: transaction.id,
        statusID: 2,
        description: 'Set back to Pending',
      });
      toast({
        title: 'Successfully updated transaction status to Pending!',
        variant: 'success',
      });
    } catch {
      toast({
        title: 'An error occurred while updating the transaction status',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [confirm, makeUpdateStatus, transaction.id]);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          disabled={isLoading}
          isLoading={isLoading}
          variant="outline"
          className="px-2 py-2"
          onClick={requestUpdateStatus}
        >
          <ArrowLeftIcon />
        </Button>
      </TooltipTrigger>
      <TooltipContent>Set back to Pending</TooltipContent>
    </Tooltip>
  );
};

export const TransactionActions = ({
  transaction,
  shouldDeleteAfterUpdate,
  page,
}: { transaction: Transaction; shouldDeleteAfterUpdate?: boolean } & Pick<
  ComponentProps<typeof EditTransactionAction>,
  'page'
>) => {
  const loggedUserId = useAuth().user.id;
  const currentUserQuery = useUserQuery({
    filters: {
      id: loggedUserId,
    },
    select(data) {
      return {
        ...data,
        role: data.permissionGroups.find(
          (p) => p.type === 'role' && (p.name === 'Manager' || p.name === 'AccountsPayable'),
        )?.name,
        isAccountsPayableAdmin: !!data.permissionGroups.find(
          (p) => p.name === 'AccountsPayableAdmin',
        )?.name,
        isReceiptReviewer: !!data.permissionGroups.find(
          (p) => p.name === PERMISSION_NAME_RECEIPT_REVIEW,
        ),
      };
    },
  });

  const isOwner =
    loggedUserId == transaction.ownerId || loggedUserId == transaction.assignedEmployeeId;
  const isManager =
    currentUserQuery.data?.role == 'Manager' || !!currentUserQuery.data?.isReceiptReviewer;
  const isAdmin = currentUserQuery.data?.isAccountsPayableAdmin;

  const editAction = (
    <EditTransactionAction
      page={page}
      transaction={transaction}
      changeCreator={!!currentUserQuery.data?.isAccountsPayableAdmin}
    />
  );

  const copyAction = <CopyTransactionInfoAction transaction={transaction} />;

  const uploadAction = <UploadPhotoForTransactionAction transaction={transaction} />;

  const transactionGalleryAction = (
    <TransactionGalleryAction
      transactionId={transaction.id}
      canDelete={isOwner || isManager || isAdmin}
      title="Transaction Uploads"
      waiverDisplay
    />
  );

  const logAction = <ViewTransactionLogsActions transactionId={transaction.id} />;

  const noteAction = (
    <Popover>
      <Tooltip>
        <PopoverTrigger asChild>
          <TooltipTrigger asChild>
            <Button className="px-2 py-2" disabled={!transaction.notes} variant="outline">
              <TextAlignLeftIcon />
            </Button>
          </TooltipTrigger>
        </PopoverTrigger>
        <TooltipContent>View Notes</TooltipContent>
      </Tooltip>
      <PopoverContent className="max-w-max">{transaction.notes}</PopoverContent>
    </Popover>
  );

  const rejectAction = <RejectTransactionAction transaction={transaction} />;
  const assignAction = <AssignTransactionAction transaction={transaction} />;
  const deleteAction = <DeleteTransactionAction transaction={transaction} />;
  const pendingAction =
    transaction.statusId !== 2 ? (
      <SetTransactionToPendingAction
        transaction={transaction}
        shouldDeleteAfterUpdate={shouldDeleteAfterUpdate}
      />
    ) : null;

  const commonActions = (
    <>
      {transactionGalleryAction}
      {noteAction}
      {logAction}
      {copyAction}
    </>
  );

  if (transaction.statusId == 2) {
    // pending
    const canEdit = isOwner || isManager || isAdmin;
    return (
      <>
        {commonActions}
        {canEdit ? editAction : null}
        {isManager || isAdmin || isOwner ? deleteAction : null}
        {isManager || isAdmin || isOwner ? uploadAction : null}
        {isManager || isAdmin ? assignAction : null}
        {isManager || isAdmin ? (
          <ApproveTransactionAction
            transaction={transaction}
            shouldDeleteAfterUpdate={shouldDeleteAfterUpdate}
          />
        ) : null}
        {isManager || isAdmin ? rejectAction : null}
        {isAdmin ? pendingAction : null}
        <TransactionLineItemsAction transaction={transaction} readonly={!canEdit} />
      </>
    );
  }

  if (transaction.statusId == 3) {
    // approved
    const canEdit = isManager || isAdmin;
    return (
      <>
        {commonActions}
        {canEdit ? editAction : null}
        {isManager || isAdmin ? uploadAction : null}
        {isManager || isAdmin ? assignAction : null}
        {isManager || isAdmin ? rejectAction : null}
        {isAdmin ? (
          <ProcessTransactionAction
            transaction={transaction}
            shouldDeleteAfterUpdate={shouldDeleteAfterUpdate}
          />
        ) : null}
        {isAdmin ? deleteAction : null}
        {isAdmin ? pendingAction : null}
        <TransactionLineItemsAction transaction={transaction} readonly={!canEdit} />
      </>
    );
  }

  if (transaction.statusId === 4) {
    // rejected
    const canEdit = isOwner || isManager || isAdmin;
    return (
      <>
        {commonActions}
        {canEdit ? editAction : null}
        {isOwner || isAdmin || isManager ? deleteAction : null}
        {isOwner || isAdmin || isManager ? uploadAction : null}
        {!isAdmin && (isOwner || isManager) ? (
          <SubmitTransactionAction
            transaction={transaction}
            shouldDeleteAfterUpdate={shouldDeleteAfterUpdate}
          />
        ) : null}
        {isManager || isAdmin ? assignAction : null}
        {isAdmin ? pendingAction : null}
        <TransactionLineItemsAction transaction={transaction} readonly={!canEdit} />
      </>
    );
  }

  if (transaction.statusId == 5) {
    // processed
    const canEdit = isAdmin;
    return (
      <>
        {commonActions}
        {canEdit ? editAction : null}
        {isAdmin ? uploadAction : null}
        {isAdmin ? deleteAction : null}
        {isAdmin ? pendingAction : null}
        <TransactionLineItemsAction transaction={transaction} readonly={!(isAdmin || isManager)} />
      </>
    );
  }

  console.warn(`No defined actions for transaction status ${transaction.statusId}`);
  return commonActions;
};

const getRejectTxnBody = (
  reason: string,
  amount: number,
  description: string,
  vendor: string,
): string => {
  return `
<body>
  <table style="width:70%;">
    <thead>
      <th style="text-align:left;">Reason</th>
      <th style="text-align:left;">Amount</th>
      <th style="text-align:left;">Info</th>
    </thead>
    <tbody>
      <tr>
        <td>${reason}</td>
        <td>${prettyMoney(amount)}</td>
        <td>${description}${vendor != '' ? ` - ${vendor}` : ''}</td>
      </tr>
    </tbody>
  </table>
  <a href="https://live.kalosflorida.com/transactions">Go to receipts</a>
</body>`;
};
